import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ProductQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  categories?: string[];
  minPrice?: number;
  maxPrice?: number;
  sortBy?: 'featured' | 'priceLow' | 'priceHigh' | 'newest';
  featured?: boolean;
  newArrival?: boolean;
  onDiscount?: boolean;
  language?: string;
}

interface Product {
  id: string;
  name: { en: string; sv: string; ar: string };
  price: number;
  image: string;
  category: string;
  featured: boolean;
  newArrival: boolean;
  on_discount: boolean;
  discount_percentage: number | null;
  discount_start_date: string | null;
  discount_end_date: string | null;
  stock_quantity: number;
}

interface ProductsResponse {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

/**
 * Hook for server-side paginated product queries.
 * Uses edge function for filtering/sorting/pagination at database level.
 */
export const useServerProducts = (params: ProductQueryParams) => {
  const queryKey = ['server-products', params];

  return useQuery({
    queryKey,
    queryFn: async (): Promise<ProductsResponse> => {
      const { data, error } = await supabase.functions.invoke('get-products', {
        body: params
      });

      if (error) throw error;
      return data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook for infinite scroll pagination of products.
 */
export const useInfiniteProducts = (params: Omit<ProductQueryParams, 'page'>) => {
  return useInfiniteQuery({
    queryKey: ['infinite-products', params],
    queryFn: async ({ pageParam = 1 }): Promise<ProductsResponse> => {
      const { data, error } = await supabase.functions.invoke('get-products', {
        body: { ...params, page: pageParam }
      });

      if (error) throw error;
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.hasMore) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook for homepage data - fetches only featured and new arrival products.
 * Uses direct Supabase query with LIMIT for efficiency.
 * Deferred loading to prioritize LCP render on mobile.
 */
export const useServerHomepageData = () => {
  return useQuery({
    queryKey: ['server-homepage-data'],
    queryFn: async () => {
      // Parallel fetch featured + new arrivals with LIMIT
      const [featuredRes, newArrivalsRes, contentRes] = await Promise.all([
        supabase
          .from('products')
          .select('id, name_en, name_sv, name_ar, price, image, category_id, featured, new_arrival, on_discount, discount_percentage, discount_start_date, discount_end_date, stock_quantity')
          .eq('visible', true)
          .eq('featured', true)
          .limit(6),
        supabase
          .from('products')
          .select('id, name_en, name_sv, name_ar, price, image, category_id, featured, new_arrival, on_discount, discount_percentage, discount_start_date, discount_end_date, stock_quantity')
          .eq('visible', true)
          .eq('new_arrival', true)
          .limit(6),
        supabase.from('homepage_content').select('*').maybeSingle()
      ]);

      if (featuredRes.error) throw featuredRes.error;
      if (newArrivalsRes.error) throw newArrivalsRes.error;

      // Get all unique product IDs for image lookup
      const allProducts = [...(featuredRes.data || []), ...(newArrivalsRes.data || [])];
      const productIds = [...new Set(allProducts.map(p => p.id))];

      // Fetch main images only for these products
      let imageMap = new Map<string, string>();
      if (productIds.length > 0) {
        const { data: mainImages } = await supabase
          .from('product_images')
          .select('product_id, image_url')
          .eq('is_main', true)
          .in('product_id', productIds);

        imageMap = new Map((mainImages || []).map(img => [img.product_id, img.image_url]));
      }

      const transform = (p: any) => ({
        id: p.id,
        name: { en: p.name_en, sv: p.name_sv, ar: p.name_ar },
        price: p.price,
        image: imageMap.get(p.id) || p.image,
        category: p.category_id,
        featured: p.featured,
        newArrival: p.new_arrival,
        on_discount: p.on_discount,
        discount_percentage: p.discount_percentage,
        discount_start_date: p.discount_start_date,
        discount_end_date: p.discount_end_date,
        stock_quantity: p.stock_quantity || 0
      });

      return {
        featuredProducts: (featuredRes.data || []).map(transform),
        newArrivals: (newArrivalsRes.data || []).map(transform),
        heroContent: contentRes.data
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    // Defer initial fetch to prioritize hero LCP on mobile
    refetchOnMount: 'always',
  });
};

/**
 * Hook for max price (for price range slider).
 * Single lightweight query.
 */
export const useMaxPrice = () => {
  return useQuery({
    queryKey: ['max-price'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('price')
        .eq('visible', true)
        .order('price', { ascending: false })
        .limit(1)
        .single();

      if (error) return 1000;
      return Math.ceil((data?.price || 1000) / 100) * 100;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook for categories.
 */
export const useServerCategories = () => {
  return useQuery({
    queryKey: ['server-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name_en, name_sv, name_ar');

      if (error) throw error;

      return (data || []).map((c: any) => ({
        id: c.id,
        name: { en: c.name_en, sv: c.name_sv, ar: c.name_ar }
      }));
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};
